import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    Bell,
    Envelope,
    ExclamationCircle,
    Eye,
    EyeSlash,
    Lock,
    ShieldCheck
} from 'react-bootstrap-icons';
import logo from '../../assets/img/logo.png';
import LoginPopup from '../LoginPopup';

import './style.css';

function Login(props) {
    const [keepLogin, setKeepLogin] = useState(false);
    const [email, setEmail] = useState('');
    const [validationEmail, setValidationEmail] = useState(false);
    const [password, setPassword] = useState('');
    const [validationPassword, setValidationPassword] = useState(false);
    const [error, setError] = useState();
    const [loading, setLoading] = useState(false);
    const [passwordShown, setPasswordShown] = useState(false);
    const [popup, setPopup] = useState(null);
    const [lockUntil, setLockUntil] = useState(0);

    const isEnterprise = props.variant === 'enterprise';

    // buka kunci tombol Masuk saat waktu tunggu habis
    useEffect(() => {
        if (!lockUntil) return;
        const sisa = lockUntil - Date.now();
        if (sisa <= 0) {
            setLockUntil(0);
            return;
        }
        const t = setTimeout(() => setLockUntil(0), sisa);
        return () => clearTimeout(t);
    }, [lockUntil]);

    const locked = lockUntil > Date.now();

    const doLogin = () => {
        const data = { email, password, keepLogin };
        setLoading(true);
        setValidationEmail(false);
        setValidationPassword(false);
        setError(undefined);

        fetch('/_api/main/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        })
            .then((res) => res.json().catch(() => ({})).then((body) => ({ status: res.status, body })))
            .then(({ status, body }) => {
                if (body.success) {
                    window.location.href = body.redirect_uri;
                    return;
                }

                // kena cooldown / blokir (HTTP 429)
                if (status === 429 || body.rate_limited) {
                    const detik = parseInt(body.retry_after, 10) || 5;
                    const endAt = Date.now() + detik * 1000;
                    setLockUntil(endAt);
                    setPopup({ type: detik <= 10 ? 'cooldown' : 'block', endAt });
                    setLoading(false);
                    return;
                }

                if (body.error) {
                    setError(body.error.code);
                    // warning di percobaan ke-5 (cadangan: baca juga dari jumlah percobaan)
                    if (body.error.warning || Number(body.error.attempt) === 5) {
                        setPopup({ type: 'warning' });
                    }
                }

                setLoading(false);
            })
            .catch(() => {
                setError(true);
                setLoading(false);
            });
    };

    const doValidation = () => {
        const emailInvalid = email.trim() === '';
        const passwordInvalid = password === '';

        setValidationEmail(emailInvalid);
        setValidationPassword(passwordInvalid);
        setError(undefined);

        if (!emailInvalid && !passwordInvalid) {
            doLogin();
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!loading && !(lockUntil > Date.now())) {
            doValidation();
        }
    };

    const popupEl = popup ? (
        <LoginPopup
            type={popup.type}
            endAt={popup.endAt}
            onClose={() => setPopup(null)}
            onFinish={() => setPopup({ type: 'done' })}
        />
    ) : null;

    if (!isEnterprise) {
        return (
            <div className="main-container" data-page={props.appName} data-aos="fade-up">
                {popupEl}
                <div className="form-title mb-3">
                    <h5>Masuk</h5>
                    <p>Silahkan masuk menggunakan akun kamu</p>
                </div>
                <div className="main-form">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-2">
                            <label htmlFor="email">Email</label>
                            <input value={email} onChange={(event) => setEmail(event.target.value)} type="text" name="email" id="email" placeholder="Masukkan email / username" className={error === 40001 ? 'form-control px-3 is-invalid' : 'form-control px-3'} required />
                            {validationEmail && <div className="invalid-feedback d-block"><ExclamationCircle /> Email / username tidak boleh kosong</div>}
                            {error === 40001 && <div className="invalid-feedback d-block"><ExclamationCircle size="14" /> Email tidak ditemukan</div>}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="password">Password</label>
                            <div className="d-flex input-password">
                                <input value={password} onChange={(event) => setPassword(event.target.value)} type={passwordShown ? 'text' : 'password'} name="password" id="password" placeholder="Masukkan password" className={error === 40002 ? 'form-control px-3 is-invalid' : 'form-control px-3'} required />
                                <button type="button" className="legacy-password-toggle" onClick={() => setPasswordShown((shown) => !shown)} aria-label={passwordShown ? 'Sembunyikan password' : 'Tampilkan password'}>
                                    {passwordShown ? <EyeSlash /> : <Eye />}
                                </button>
                            </div>
                            {validationPassword && <div className="invalid-feedback d-block"><ExclamationCircle /> Password tidak boleh kosong</div>}
                            {error === 40002 && <div className="invalid-feedback d-block"><ExclamationCircle size="14" /> Password tidak sesuai</div>}
                        </div>
                        <div className="mb-4 d-flex">
                            <div className="col-6 form-check">
                                <input checked={keepLogin} onChange={(event) => setKeepLogin(event.target.checked)} type="checkbox" name="keepLogin" id="keepLogin" className="form-check-input" />
                                <label htmlFor="keepLogin">Ingat saya</label>
                            </div>
                            <div className="col-6 forgot-password"><Link className="link" to="/forgot">Lupa password?</Link></div>
                        </div>
                        <button type="submit" className="form-control mb-2" disabled={loading || locked}>
                            {loading ? <span className="loading-container"><span className="spinner-border text-light loading" role="status" aria-hidden="true"></span> Tunggu sebentar</span> : 'Masuk'}
                        </button>
                        <div className="col-12 text-center"><span data-testid="not-have-account">Belum punya akun?</span><br /><span data-testid="not-have-account">Silahkan hubungi sekolah</span></div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="enterprise-auth-card">
            {popupEl}
            <a className="enterprise-auth-announcement" href="/skl/">
                <Bell aria-hidden="true" />
                <span>Informasi kelulusan SMP</span>
                <strong>Lihat <span aria-hidden="true">→</span></strong>
            </a>

            <div className="enterprise-auth-brand">
                <img src={logo} alt="Lambang Kota Padang" />
                <span>
                    <strong>KOTA PADANG</strong>
                    <small>BLENDED LEARNING</small>
                </span>
            </div>

            <div className="enterprise-auth-heading">
                <h2>Masuk ke akun Anda</h2>
                <p>Gunakan akun sekolah yang telah terdaftar.</p>
            </div>

            <form className="enterprise-auth-form" onSubmit={handleSubmit} noValidate>
                <div className="enterprise-field-group">
                    <label htmlFor="enterprise-email">Email atau username</label>
                    <div className={`enterprise-input-wrap ${(validationEmail || error === 40001) ? 'has-error' : ''}`}>
                        <Envelope aria-hidden="true" />
                        <input
                            id="enterprise-email"
                            name="email"
                            type="text"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            placeholder="Masukkan email atau username"
                            autoComplete="username"
                            autoCapitalize="none"
                            aria-invalid={validationEmail || error === 40001}
                            aria-describedby={(validationEmail || error === 40001) ? 'enterprise-email-error' : undefined}
                        />
                    </div>
                    {validationEmail && <p id="enterprise-email-error" className="enterprise-field-error"><ExclamationCircle /> Email atau username tidak boleh kosong</p>}
                    {error === 40001 && <p id="enterprise-email-error" className="enterprise-field-error"><ExclamationCircle /> Email tidak ditemukan</p>}
                </div>

                <div className="enterprise-field-group">
                    <label htmlFor="enterprise-password">Password</label>
                    <div className={`enterprise-input-wrap ${(validationPassword || error === 40002) ? 'has-error' : ''}`}>
                        <Lock aria-hidden="true" />
                        <input
                            id="enterprise-password"
                            name="password"
                            type={passwordShown ? 'text' : 'password'}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            placeholder="Masukkan password"
                            autoComplete="current-password"
                            aria-invalid={validationPassword || error === 40002}
                            aria-describedby={(validationPassword || error === 40002) ? 'enterprise-password-error' : undefined}
                        />
                        <button
                            type="button"
                            className="enterprise-password-toggle"
                            onClick={() => setPasswordShown((shown) => !shown)}
                            aria-label={passwordShown ? 'Sembunyikan password' : 'Tampilkan password'}
                            aria-pressed={passwordShown}
                        >
                            {passwordShown ? <EyeSlash aria-hidden="true" /> : <Eye aria-hidden="true" />}
                        </button>
                    </div>
                    {validationPassword && <p id="enterprise-password-error" className="enterprise-field-error"><ExclamationCircle /> Password tidak boleh kosong</p>}
                    {error === 40002 && <p id="enterprise-password-error" className="enterprise-field-error"><ExclamationCircle /> Password tidak sesuai</p>}
                </div>

                {error === true && (
                    <p className="enterprise-request-error" role="alert">
                        <ExclamationCircle /> Koneksi bermasalah. Silakan coba kembali.
                    </p>
                )}

                <div className="enterprise-auth-options">
                    <label className="enterprise-remember">
                        <input checked={keepLogin} onChange={(event) => setKeepLogin(event.target.checked)} type="checkbox" name="keepLogin" />
                        <span>Ingat saya</span>
                    </label>
                    <Link to="/forgot">Lupa password?</Link>
                </div>

                <button type="submit" className="enterprise-submit" disabled={loading || locked}>
                    {loading ? (
                        <span className="enterprise-loading"><span className="spinner-border" role="status" aria-hidden="true"></span> Tunggu sebentar</span>
                    ) : 'Masuk'}
                </button>
            </form>

            <div className="enterprise-auth-footer">
                <div className="enterprise-auth-divider"><span>atau</span></div>
                <p>Belum memiliki akun? <span>Hubungi sekolah</span></p>
                <div><ShieldCheck aria-hidden="true" /> Koneksi aman dan terenkripsi</div>
            </div>
        </div>
    );
}

export default Login;